import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import { SENDAFooter } from '../components/SENDAFooter';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await login(email, password);
        setLoading(false);

        if (result.success) {
            if (result.data.must_change_password) {
                navigate('/cambiar-password');
            } else if (result.data.role === 'mentor') {
                navigate('/mentor');
            } else {
                navigate('/usuario');
            }
        } else {
            setError(result.error);
        }
    };

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col relative overflow-hidden">
            {/* Ambient background */}
            <div className="absolute inset-0">
                <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-[#3E5F6E]/[0.03] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#5FAF9D]/[0.04] rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
            </div>
            
            <div className="flex-1 flex items-center justify-center p-4 relative z-10">
                <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm border border-[#DCE3E8]/60 shadow-2xl shadow-[#3E5F6E]/[0.06] rounded-3xl animate-fade-in-up">
                    <CardHeader className="space-y-5 text-center pb-8 pt-10">
                        <div className="mx-auto">
                            <img src="/logo-senda.png" alt="SENDA" className="h-20 w-auto mx-auto" data-testid="login-logo" />
                        </div>
                        <div>
                            <CardTitle className="text-2xl font-light text-[#1F2A30] font-['Outfit'] tracking-tight">Bienvenido a SENDA</CardTitle>
                            <CardDescription className="text-[#7A8A93] mt-2">
                                Plataforma para superar la Procrastinación
                            </CardDescription>
                        </div>
                    </CardHeader>
                    
                    <CardContent className="pb-10">
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div className="flex items-center gap-2 p-4 bg-[#D07F7E]/10 border border-[#D07F7E]/20 rounded-xl text-[#D07F7E]" data-testid="login-error-message">
                                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                                    <span className="text-sm">{error}</span>
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-sm font-medium text-[#3E5F6E]">Correo electrónico</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="tu@email.com"
                                    className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl h-12"
                                    required
                                    data-testid="login-email-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-sm font-medium text-[#3E5F6E]">Contrasena</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl pr-10 h-12"
                                        required
                                        data-testid="login-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7A8A93] hover:text-[#4A5B63] transition-colors"
                                        data-testid="toggle-password-visibility"
                                    >
                                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                    </button>
                                </div>
                            </div>
                            
                            <Button
                                type="submit"
                                className="w-full bg-[#3E5F6E] hover:bg-[#2F4A57] text-white h-12 rounded-xl font-medium transition-all shadow-lg shadow-[#3E5F6E]/20 hover:shadow-xl hover:shadow-[#3E5F6E]/30"
                                disabled={loading}
                                data-testid="login-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Iniciando sesión...
                                    </>
                                ) : (
                                    'Iniciar sesión'
                                )}
                            </Button>
                            
                            <div className="text-center">
                                <Link 
                                    to="/recuperar-password" 
                                    className="text-sm text-[#5FAF9D] hover:text-[#4A9A89] transition-colors font-medium"
                                    data-testid="forgot-password-link"
                                >
                                    ¿Olvidaste tu contrasena?
                                </Link>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
            <SENDAFooter />
        </div>
    );
}
